<script setup lang = "ts">
import type {PropType} from "vue";
import type {CVCard} from "~/interfaces/CVCard";
import moment from "moment/moment";
import CVToggleHeart from "~/components/common/button/CVToggleHeart.vue";
import FooterContactCard from "~/components/cv/card/FooterContactCard.vue";

const props = defineProps({
	product: {type: Object as PropType<CVCard>, required: true},
	hideFooter: {type: Boolean, required: false, default: false},
	isVertical: {type: Boolean, default: false},
	breakpoint: {type: Number, default: 600},
	maxWidth: {type: String, default: '700px'}
})

const isMounted = ref(false)

const isAuthenticated = useCookie('isAuth')
const {favDataProcess, toggleProductFav} = useHelpers()

onMounted(() => {
	isMounted.value = true
})
const {formatCurrency} = useNumberFormat()
const daysLeft = props.product?.daysLeft ?? moment().diff(moment(props.product?.created_at), "days")
const contactForm = ref(false)

</script>

<template>
	<div
			:class = "[`${isVertical || $q.screen.width<=breakpoint?($q.screen.width<=breakpoint?'flex flex-col min-w-[320px] w-full max-w-[350px] flex-basis-1':'flex flex-col max-w-[350px]'):'flex flex-row w-full h-[230px] max-w-['+maxWidth+'] min-w-[400px]'} relative no-wrap rounded border-gray-100 border`]">
		<div class = "relative z-0">
			<div
					:class = "isVertical || $q.screen.width<=breakpoint?'h-[210px] max-w-[370px] min-w-[350px]':'h-full w-[220px] overflow-hidden'">
				<CVCarouselFixed :images = "product.pictures??[]"
												 :is-vertical = "isVertical"
												 :breakpoint = "breakpoint"
												 :has-navigation = "false"
				
				/>
			</div>
			<div v-if = "false" class = "bg-gray-900/60 p-2 absolute rounded-sm top-[5px] left-[5px]">
				<p class = "text-xs font-light text-white">{{ daysLeft > 0 ? daysLeft : 1 }}
																									 {{ daysLeft <= 1 ? 'día' : 'días' }} en compralovendelo</p>
			</div>
			<div v-if = "product.isAdvertisement" class = "bg-gray-900/80 p-2 absolute rounded-sm bottom-[5px] left-[5px]">
				<p class = "text-xs font-light text-white">Anuncio</p>
			</div>
		</div>
		<div
				:class = "`${isVertical || $q.screen.width<=breakpoint?'':'min-w-[150px] min-h-[225px]'} py-2 h-full relative w-full overflow-hidden`"
				:style = "[{paddingBottom:`${(isVertical || $q.screen.width<=breakpoint) && product.type!=='car'? 62 : 0}px`},{height:`${product.type=='car'?'225px':'auto'} `}]">
			<div class = "px-2 flex flex-row flex-grow justify-between w-full no-wrap">
				<div class = "flex flex-row items-center">
					<div class = "bg-primary py-1 px-2 mr-2 rounded-sm mb-2">
						<p class = "text-xs font-light text-white p-0 m-0">{{ product.status }}</p>
					</div>
					<div v-if = "product.type=='property'" class = "bg-secondary py-1 px-2 rounded-sm">
						<p class = "text-xs font-light text-white p-0 m-0">{{ product.extras.type }}</p>
					</div>
				</div>
				<div>
					<CVToggleHeart :is-authenticated = "!!isAuthenticated"
												 v-if = "!product.originType?.includes('EXT')"
												 style = "transform: scale(0.6)"
												 color = "#E8200E"
												 class = "overflow-hidden"
												 :id = "product._id"
												 :disabled-input = "favDataProcess.includes(product._id)?true:undefined"
												 :animate-flag = "isMounted"
												 :toggle-status = "!!product.hasLike"
												 @toggle-fav = "(status)=>toggleProductFav(status,product._id)"/>
				</div>
			</div>
			
			<div class = "px-2 flex flex-row items-center justify-between items-center">
				<NuxtLink :to = "`/${product.type=='car'?'carros':'bienes-raices'}/${product.link}`"
									class = "text-lg mb-1 font-medium max-h-[48px] text-wrap truncate overflow-hidden overflow-ellipsis flex-grow"
									style = "display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical;line-height: 1.1; overflow:hidden !important;">
					{{ product.title }}
				</NuxtLink>
			</div>
			<div class = "px-2 flex flex-row flex-nowrap items-center" v-if = "product.address && product.address!==''">
				<img class = "sm:h-[16px] sm:w-[16px] h-[14px] w-[14px] mr-1"
						 :src = "`${useNuxtApp().$domain}/icons/location_RED.svg`"/>
				<p class = "text-[12px] sm:text-[14px] font-light text-wrap truncate overflow-hidden overflow-ellipsis flex-grow"
					 style = "display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical;line-height: 1.1">
					{{ product.address }}
				</p>
			</div>
			<p v-if = "product.agent && product.type=='property'" class = "px-2 text-xs font-light mt-1">
				Listed by: {{ product.agent?.name }}
			</p>
			<div class = "px-2 flex flex-row justify-between items-center no-wrap max-w-[350px]">
				<div class = "relative mt-2 flex-grow">
					<div class = "font-medium text-xs relative mb-[-5px]">Precio:</div>
					<p class = "font-extralight text-xl sm:text-2xl md:text-2xl flex items-center">
						{{ formatCurrency(product.price ?? 0) }}
						<div v-html = "product.monthly"/>
					</p>
				
				</div>
				<div class = "no-wrap">
					<q-btn padding = "5px 10px" size = "sm" class = "mt-[15px]" outline color = "primary"
								 @click = "navigateTo(`/${product.type=='car'?'carros':'bienes-raices'}/${product.link}`)">
						Ver {{ product.type == 'car' ? 'Carro' : (product.type == 'property' ? 'Propiedad' : '') }}
					</q-btn>
				</div>
			</div>
			<div v-if = "product.type=='car'"
					 class = "mx-2 flex flex-row flex-nowrap pt-2  sm:pt-1 pb-2 max-w-[350px] min-h-[40px]">
				<q-chip v-for = "(feature,index) in product.features"
								square
								color = "primary"
								size = "12px"
								class = "text-white"
								:class = "index<((product.features?.length??0)-1)?'':'invisible-in-sm'"
								:label = "feature"/>
			</div>
			
			<!-- Footer -->
			<div v-if = "product.type=='property'"
					 :class = "`${isVertical || $q.screen.width<=breakpoint?'w-full bottom-0 px-2 left-[0px]':'max-w-[350px] bottom-[-5px]   left-[0px]'}  flex flex-row items-center py-2 justify-between w-full px-0 px-3 absolute`">
				<div class = "flex flex-row items-center">
					<img class = " w-[30px] sm:w-[30px] h-[30px]  sm:h-[30px]"
							 :src = "`${useNuxtApp().$domain}/icons/house-area.svg`"/>
					<div class = "flex flex-col ml-1">
						<p class = "text-[10] sm:text-[12px] font-normal mb-[-5px]">Área:</p>
						<p class = "text-[11px] sm:text-[12px] font-light">{{ product.extras?.area }}<span
								class = "super-index text-[9px]">2</span></p>
					</div>
				</div>
				<div v-if = "parseInt(`${product.extras?.rooms??0}`) > 0" class = "flex flex-row items-center">
					<img class = " w-[25px] sm:w-[30] h-[25px]  sm:h-[30]"
							 :src = "`${useNuxtApp().$domain}/icons/bedroom.svg`"/>
					<div class = "flex flex-col ml-1">
						<p class = "text-[10] sm:text-[12px]  font-normal mb-[-5px]">Habit.:</p>
						<p class = "text-[10] sm:text-[12px] font-light">{{ product.extras?.rooms }} </p>
					</div>
				</div>
				<div v-if = "parseInt(`${product.extras?.bathrooms??0}`) > 0" class = "flex flex-row items-center">
					<img class = " w-[25px] sm:w-[30] h-[25px]  sm:h-[30]"
							 :src = "`${useNuxtApp().$domain}/icons/bathroom.svg`"/>
					<div class = "flex flex-col ml-1">
						<p class = "text-[10] sm:text-[12px]  font-normal mb-[-5px]">Baños:</p>
						<p class = "text-[10] sm:text-[12px]  font-light">{{ product.extras?.bathrooms }} </p>
					</div>
				</div>
			
			</div>
			<FooterContactCard class = "absolute bottom-0 left-0" :user_id = "product.user_id" data_type = "car"
												 :is-horizontal = "!isVertical"
												 v-if = "product.type=='car'"/>
		
		</div>
	</div>
</template>

<style scoped>

</style>